import { FC, ReactNode, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CircleAlert, Network } from 'lucide-react';

import { Modal } from '@/components/modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/loading-spinner';
import { ForceGraph } from '@/components/context-graph-viewer/force-graph';
import { GraphSelection } from '@/components/context-graph-viewer/helpers/graph-types';
import {
  VIEW_STATE_PARAM,
  decodeViewState,
} from '@/components/context-graph-viewer/helpers/view-state';
import { contextGraphNodeSearchKey } from '@/hooks/queries/use-context-graph-node-search';
import {
  contextGraphVisualizationKey,
  useContextGraphVisualization,
} from '@/hooks/queries/use-context-graph-visualization';
import { useOrgStore } from '@/stores/org-store';
import {
  ContextGraphListItem,
  ContextGraphNodeSearchResult,
  ContextGraphVisualization,
} from '@/types/context-graph-types';

interface ContextGraphViewerModalProps {
  graph: ContextGraphListItem;
  open: boolean;
  canEdit?: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ContextGraphViewerModal: FC<ContextGraphViewerModalProps> = ({
  graph,
  open,
  canEdit = false,
  onOpenChange,
}) => {
  const queryClient = useQueryClient();
  const orgId = useOrgStore((state) => state.currentOrg?.orgid ?? null);
  const { data, isLoading, isError, refetch } = useContextGraphVisualization(
    open ? graph.graph_id : null,
  );

  const handleGraphMutated = useCallback(
    (removed: GraphSelection) => {
      const key = contextGraphVisualizationKey(orgId, graph.graph_id);
      queryClient.setQueryData<ContextGraphVisualization>(key, (current) => {
        if (!current) return current;
        if (removed.kind === 'node') {
          const nodeId = removed.node.id;
          const edges = current.edges.filter(
            (edge) => edge.source !== nodeId && edge.target !== nodeId,
          );
          return {
            ...current,
            nodes: current.nodes.filter((node) => node.id !== nodeId),
            edges,
            node_count: Math.max(current.node_count - 1, 0),
            edge_count: Math.max(
              current.edge_count - (current.edges.length - edges.length),
              0,
            ),
          };
        }
        return {
          ...current,
          edges: current.edges.filter((edge) => edge.id !== removed.link.id),
          edge_count: Math.max(current.edge_count - 1, 0),
        };
      });
      // Cached node-search payloads (keepPreviousData + staleTime) are merged
      // back into the scene on truncated graphs — patch them too so a removed
      // entity can't be spliced back in from a stale search result.
      const searchKey = contextGraphNodeSearchKey(orgId, graph.graph_id);
      queryClient.setQueriesData<ContextGraphNodeSearchResult>(
        { queryKey: searchKey },
        (current) => {
          if (!current) return current;
          if (removed.kind === 'node') {
            const nodeId = removed.node.id;
            return {
              ...current,
              nodes: current.nodes.filter((node) => node.id !== nodeId),
              edges: current.edges.filter(
                (edge) => edge.source !== nodeId && edge.target !== nodeId,
              ),
              matched_ids: current.matched_ids.filter((id) => id !== nodeId),
            };
          }
          return {
            ...current,
            edges: current.edges.filter((edge) => edge.id !== removed.link.id),
          };
        },
      );
      // Server truth may differ (degree-capped export); refetch on next open.
      queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' });
      queryClient.invalidateQueries({
        queryKey: searchKey,
        refetchType: 'none',
      });
    },
    [queryClient, orgId, graph.graph_id],
  );

  const initialViewState = useMemo(() => {
    if (!open) return null;
    return decodeViewState(
      new URLSearchParams(window.location.search).get(VIEW_STATE_PARAM),
    );
    // Parsed once per open so in-viewer URL syncing doesn't re-restore state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, graph.graph_id]);

  const isEmpty = Boolean(data && data.nodes.length === 0);

  let content: ReactNode = null;
  if (isLoading) {
    content = (
      <div className='text-muted-foreground flex h-full flex-col items-center justify-center gap-3'>
        <LoadingSpinner />
        <p className='text-sm'>Loading graph…</p>
      </div>
    );
  } else if (isError) {
    content = (
      <div className='text-muted-foreground flex h-full flex-col items-center justify-center gap-3'>
        <CircleAlert className='size-8' />
        <p className='text-sm'>Couldn&apos;t load this graph.</p>
        <Button variant='outline' size='sm' onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  } else if (isEmpty) {
    content = (
      <div className='text-muted-foreground flex h-full flex-col items-center justify-center gap-3 text-center'>
        <Network className='size-8' />
        <p className='text-sm font-medium'>This graph is empty</p>
        <p className='max-w-sm text-sm'>
          Entities and facts will appear here as data is added to the graph.
        </p>
      </div>
    );
  } else if (data) {
    content = (
      // key remounts the viewer when switching graphs while the modal stays
      // open (cached data renders instantly), so per-graph refs like
      // initialViewConsumedRef and restoredSelectionRef never leak across
      // graphs.
      <ForceGraph
        key={graph.graph_id}
        nodes={data.nodes}
        edges={data.edges}
        graphId={graph.graph_id}
        graphName={graph.name}
        truncated={data.truncated}
        initialViewState={initialViewState}
        canEdit={canEdit}
        onGraphMutated={handleGraphMutated}
      />
    );
  }

  return (
    <Modal
      title={
        <span className='flex items-center gap-2'>
          {graph.name}
          {data && !isEmpty ? (
            <span className='text-muted-foreground text-sm font-normal'>
              {data.node_count} entities · {data.edge_count} facts
            </span>
          ) : null}
          {data?.truncated ? (
            <Badge variant='secondary' className='font-normal'>
              Showing top {data.nodes.length} of {data.node_count}
            </Badge>
          ) : null}
        </span>
      }
      isDialogOpen={open}
      setIsDialogOpen={onOpenChange}
      contentClassName='h-[92vh] w-[96vw] sm:max-w-[96vw] xl:max-w-[1500px]'
    >
      <div className='h-full min-h-[60vh] w-full'>{content}</div>
    </Modal>
  );
};
